/**
 * `useZdrStatus` — read-only hook for the current customer's Zero Data
 * Retention posture.
 *
 * Lightly cached at module scope so the dozens of `<ModelSelector>` and
 * route-override dropdowns scattered through the app don't each fire
 * their own GET. The cached value lives ~30 seconds; the
 * `PrivacyComplianceCard` calls `invalidateZdrStatus()` after a successful
 * toggle so other components pick up the new state on their next render.
 *
 * ZDR has two orthogonal axes: `enabled` (the no-train contract) and
 * `region_mode` (`fastest` | `us`, data residency). The hook surfaces
 * both; most callers only care about `enabled`.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getConfig } from '../services/config';

const ZDR_REGION_FASTEST = 'fastest';
const ZDR_REGION_US = 'us';

interface ZdrStatus {
  enabled: boolean;
  region_mode: string;
  loading: boolean;
  error: string | null;
}

interface ZdrSnapshot {
  enabled: boolean;
  region_mode: string;
}

interface CacheEntry extends ZdrSnapshot {
  fetchedAt: number;
}

const CACHE_TTL_MS = 30_000;

const _normalizeRegion = (v: unknown): string =>
  v === ZDR_REGION_US ? ZDR_REGION_US : ZDR_REGION_FASTEST;

let _cache: CacheEntry | null = null;
let _inFlight: Promise<ZdrSnapshot> | null = null;
const _subscribers = new Set<(snap: ZdrSnapshot) => void>();

function _publish(snap: ZdrSnapshot) {
  _cache = { ...snap, fetchedAt: Date.now() };
  // Use Set.forEach instead of `for...of` to stay compatible with the
  // project's TS target (< ES2015 / no downlevelIteration).
  _subscribers.forEach((cb) => {
    try { cb(snap); } catch { /* swallow */ }
  });
}

/** Force-clear the cache. Call after successfully PATCHing the flag. */
export function invalidateZdrStatus() {
  _cache = null;
  _inFlight = null;
}

const _OFF: ZdrSnapshot = { enabled: false, region_mode: ZDR_REGION_FASTEST };

async function _fetchOnce(token: string | null): Promise<ZdrSnapshot> {
  if (!token) return _OFF;
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const config = getConfig();
      const resp = await fetch(`${config.apiBaseUrl}/api/v2/customers/zero-data-retention`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        // 404 / 401 / 500 — fail closed (assume OFF) so the UI doesn't
        // accidentally hide options for users without the feature.
        return _OFF;
      }
      const body = await resp.json();
      const data = body?.data ?? body;
      const snap: ZdrSnapshot = {
        enabled: !!data?.enabled,
        region_mode: _normalizeRegion(data?.region_mode),
      };
      _publish(snap);
      return snap;
    } catch {
      return _OFF;
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
}

export function useZdrStatus(): ZdrStatus {
  const { user, getIdToken } = useAuth();
  const [enabled, setEnabled] = useState<boolean>(_cache?.enabled ?? false);
  const [regionMode, setRegionMode] = useState<string>(_cache?.region_mode ?? ZDR_REGION_FASTEST);
  const [loading, setLoading] = useState<boolean>(_cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const subscriber = (snap: ZdrSnapshot) => {
      if (!cancelled) {
        setEnabled(snap.enabled);
        setRegionMode(snap.region_mode);
      }
    };
    _subscribers.add(subscriber);

    const refreshIfStale = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      const fresh = _cache && Date.now() - _cache.fetchedAt < CACHE_TTL_MS;
      if (fresh) {
        setEnabled(_cache!.enabled);
        setRegionMode(_cache!.region_mode);
        setLoading(false);
        return;
      }
      try {
        const token = await getIdToken();
        const result = await _fetchOnce(token);
        if (!cancelled) {
          setEnabled(result.enabled);
          setRegionMode(result.region_mode);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    refreshIfStale();

    return () => {
      cancelled = true;
      _subscribers.delete(subscriber);
    };
  }, [user, getIdToken]);

  return { enabled, region_mode: regionMode, loading, error };
}
